import React from 'react';

import { Box, Typography } from '@mui/material';

import {
  DisclaimerInfoFragment,
  DisclaimersListDocument,
  useDisclaimerDeleteMutation,
} from 'src/api';
import { Button, Modal } from 'src/components/common';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useModal } from 'src/hooks/shared';

export const DisclaimerDeleteModal: React.FC = () => {
  const { closeModal, isOpen, args } = useModal<{
    disclaimer: DisclaimerInfoFragment;
  }>(ModalId.DisclaimerDelete);
  const disclaimer = args?.disclaimer;

  const { createApolloContext } = useNotification();

  const [isButtonsDisabled, setIsButtonsDisabled] = React.useState(false);

  const [deleteDisclaimer] = useDisclaimerDeleteMutation({
    refetchQueries: [DisclaimersListDocument],
    context: createApolloContext({ successMessage: 'Disclaimer successfully deleted' }),
  });

  React.useEffect(() => {
    setIsButtonsDisabled(false);
  }, [isOpen]);

  const onCloseModal = React.useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleDelete = React.useCallback(async () => {
    if (!disclaimer?.id) return;

    setIsButtonsDisabled(true);
    try {
      await deleteDisclaimer({
        variables: {
          where: {
            id: disclaimer.id,
          },
        },
      });

      closeModal();
    } catch (error) {
      console.error(error);
    } finally {
      setIsButtonsDisabled(false);
    }
  }, [closeModal, deleteDisclaimer, disclaimer?.id]);

  return (
    <Modal
      title="Delete Employer Disclaimer"
      dialogProps={{
        fullWidth: true,
        onClose: onCloseModal,
        open: isOpen,
      }}
    >
      <Box>
        <Typography variant="body1" color="text.secondary">
          Delete Employer Disclaimer &quot;{disclaimer?.name}&quot;?
        </Typography>
      </Box>
      <Box display="flex" justifyContent="center" gap={2} mt={2}>
        <Button
          disabled={isButtonsDisabled}
          variant="outlined"
          color="error"
          onClick={onCloseModal}
        >
          Cancel
        </Button>

        <Button disabled={isButtonsDisabled} variant="contained" onClick={handleDelete}>
          Delete
        </Button>
      </Box>
    </Modal>
  );
};
